import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { Button, Card, DataTable, EmptyState, Input, MetricCard, PageHeader, Tag, type ColumnDef } from '../components/ui';

interface AuditItem {
  id: number;
  user_id: string;
  user_email: string | null;
  action: string;
  resource_type: string | null;
  resource_id: number | null;
  case_id: number | null;
  extra: Record<string, unknown> | null;
  created_at: string | null;
}

interface Metrics {
  total_events: number;
  unique_users: number;
  by_action: { action: string; count: number }[];
  top_users: { user_id: string; count: number }[];
}

const DATETIME = new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'medium' });
const COUNT = new Intl.NumberFormat('en-US');

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  return DATETIME.format(new Date(iso));
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id);

const AUDIT_COLUMNS: ColumnDef<AuditItem>[] = [
  {
    id: 'when',
    header: 'When',
    width: 'w-44',
    cell: (it) => <span className="text-ink-soft whitespace-nowrap">{fmtDate(it.created_at)}</span>,
  },
  {
    id: 'user',
    header: 'User',
    width: 'w-48',
    cell: (it) => (
      <span className="t-ident text-ink-soft" title={it.user_id}>
        {it.user_email || shortId(it.user_id)}
      </span>
    ),
  },
  {
    id: 'action',
    header: 'Action',
    width: 'w-48',
    cell: (it) => <span className="t-ident text-green-900">{it.action}</span>,
  },
  {
    id: 'resource',
    header: 'Resource',
    width: 'w-32',
    cell: (it) => (
      <span className="text-xs text-ink-soft">
        {it.resource_type ? `${it.resource_type}#${it.resource_id ?? '—'}` : '—'}
      </span>
    ),
  },
  {
    id: 'case',
    header: 'Case',
    width: 'w-20',
    cell: (it) => <span className="text-xs text-ink-soft">{it.case_id ?? '—'}</span>,
  },
  {
    id: 'details',
    header: 'Details',
    width: 'flex',
    minWidth: 'min-w-64',
    cell: (it) => {
      const extra = it.extra ? JSON.stringify(it.extra) : '';
      return (
        <span className="t-ident block truncate text-ink-faint" title={extra || undefined}>
          {extra}
        </span>
      );
    },
  },
];

export default function AuditLogPage() {
  const [items, setItems] = useState<AuditItem[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (actionFilter) params.action = actionFilter;
      if (userFilter) params.user_filter = userFilter;
      const [logRes, metricsRes] = await Promise.all([
        api.get('/audit-log', { params }),
        api.get('/audit-log/metrics'),
      ]);
      setItems(logRes.data.items || []);
      setMetrics(metricsRes.data);
    } catch (raw) {
      const e = raw as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load audit log');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (forbidden) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card padding="none">
          <EmptyState
            icon={Shield}
            title="Admin access required"
            description="The audit log is only visible to firm administrators. If you need access, ask your Shale administrator to add your user id to the admin allowlist."
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-control bg-green-900 text-white text-sm hover:bg-green-800 transition-colors"
              >
                <ArrowLeft size={16} />
                Back to cases
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        size="title"
        title={
          <span className="flex items-center gap-2">
            <Shield size={24} className="text-green-900" />
            Audit Log
          </span>
        }
        description="Tracks who did what and when. Never contains client names, account numbers, or transaction details."
        actions={
          <Button variant="secondary" icon={RefreshCw} loading={loading} onClick={load}>
            Refresh
          </Button>
        }
      />

      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <MetricCard label="Total events" value={COUNT.format(metrics.total_events)} />
          <MetricCard label="Unique users" value={COUNT.format(metrics.unique_users)} />
          <Card className="md:col-span-2">
            <div className="t-eyebrow mb-2">Top actions</div>
            <div className="flex flex-wrap gap-2">
              {metrics.by_action.slice(0, 8).map((a) => (
                <Tag key={a.action} tone="good" className="font-mono font-medium">
                  {a.action} · {a.count}
                </Tag>
              ))}
              {metrics.by_action.length === 0 && (
                <span className="text-sm text-ink-faint">No events yet</span>
              )}
            </div>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1">
          <Input
            size="md"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Filter by action (e.g. case.create)"
          />
        </div>
        <div className="flex-1">
          <Input
            size="md"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Filter by user id"
          />
        </div>
        <Button
          variant="secondary"
          onClick={() => { setActionFilter('error.'); setTimeout(load, 0); }}
          title="Show only worker and API errors"
          className={
            actionFilter.startsWith('error.')
              ? 'bg-danger-soft border-danger text-danger hover:bg-danger-soft'
              : 'text-ink-soft'
          }
        >
          Errors only
        </Button>
        <Button onClick={load}>Apply</Button>
      </div>

      {error ? (
        <Card padding="none">
          <div className="py-8 text-center text-sm text-danger">{error}</div>
        </Card>
      ) : (
        <DataTable
          caption="Audit log events"
          rows={items}
          columns={AUDIT_COLUMNS}
          rowKey={(it) => it.id}
          density="compact"
          loading={loading && items.length === 0}
          empty={
            <div className="py-8 text-center text-sm text-ink-faint">
              No events match the current filters.
            </div>
          }
        />
      )}

      <p className="text-xs text-ink-faint mt-4">
        Showing the {items.length} most recent entries. Use the filters above to narrow further.
      </p>
    </div>
  );
}
